<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { formatDistanceToNow } from 'date-fns';
import { useI18n } from 'vue-i18n';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  chat: {
    type: Object,
    required: true,
  },
  active: {
    type: Boolean,
    default: false,
  },
  unread: {
    type: Number,
    default: 0,
  },
});

defineEmits(['click']);

const { t } = useI18n();
const store = useStore();

// Reuse the dashboard-wide agent presence (agents/updatePresence, fed globally by
// the presence.update ActionCable event) — show online/busy/offline on the DM avatar.
const otherUserStatus = computed(() => {
  const other = props.chat.other_user;
  if (!other?.id) return null;
  const agent = store.getters['agents/getAgentById'](other.id);
  return agent?.availability_status || null;
});

const displayName = computed(() => {
  if (props.chat.chat_type === 'direct_message') {
    const other = props.chat.other_user;
    // [polish] was hardcoded 'Direct Message' — now i18n
    return other?.name || t('INTERNAL_CHATS.DIRECT_MESSAGE_FALLBACK');
  }
  if (props.chat.chat_type === 'channel') {
    // [polish] was hardcoded '#geral' — channel name comes from server
    return `#${props.chat.name || t('INTERNAL_CHATS.GENERAL_CHANNEL')}`;
  }
  return props.chat.name || '';
});

const otherUserName = computed(() => {
  const other = props.chat.other_user;
  return other?.name || t('INTERNAL_CHATS.DIRECT_MESSAGE_FALLBACK');
});

const otherUserAvatar = computed(() => {
  const other = props.chat.other_user;
  return other?.avatar_url || '';
});

const messagePreview = computed(() => {
  const content = props.chat.last_message?.content || '';
  if (!content) return '';
  return content.length > 40 ? `${content.slice(0, 40)}…` : content;
});

const lastMessageTime = computed(() => {
  if (!props.chat.last_message_at) return null;
  try {
    return formatDistanceToNow(new Date(props.chat.last_message_at), {
      addSuffix: false,
    });
  } catch {
    return null;
  }
});
</script>

<template>
  <button
    class="w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-n-alpha-1"
    :class="{ 'bg-n-alpha-1': active }"
    :aria-current="active ? 'true' : undefined"
    :aria-label="displayName"
    @click="$emit('click', chat)"
  >
    <!-- Icon / Avatar -->
    <div class="flex-shrink-0">
      <template v-if="chat.chat_type === 'channel'">
        <div
          class="size-7 rounded-md bg-n-alpha-2 flex items-center justify-center"
        >
          <span class="i-lucide-hash size-3.5 text-n-slate-10" />
        </div>
      </template>
      <template v-else-if="chat.chat_type === 'group'">
        <img
          v-if="chat.avatar_url"
          :src="chat.avatar_url"
          class="size-7 rounded-md object-cover"
          :alt="chat.name || ''"
        />
        <div
          v-else
          class="size-7 rounded-md bg-n-alpha-2 flex items-center justify-center"
        >
          <span class="i-lucide-users size-3.5 text-n-slate-10" />
        </div>
      </template>
      <template v-else>
        <Avatar
          :name="otherUserName"
          :src="otherUserAvatar"
          :size="28"
          :status="otherUserStatus"
          rounded-full
        />
      </template>
    </div>

    <!-- Content -->
    <div class="flex-1 min-w-0">
      <div class="flex items-center justify-between gap-1">
        <span
          class="text-xs font-medium truncate"
          :class="unread > 0 ? 'text-n-slate-12' : 'text-n-slate-11'"
        >
          {{ displayName }}
        </span>
        <span
          v-if="lastMessageTime"
          class="text-[10px] text-n-slate-9 flex-shrink-0"
        >
          {{ lastMessageTime }}
        </span>
      </div>
      <div class="flex items-center justify-between gap-1 mt-0.5">
        <span class="text-[11px] text-n-slate-9 truncate">
          {{ messagePreview }}
        </span>
        <!-- [polish] cap badge at 99 to prevent overflow in narrow column -->
        <span
          v-if="unread > 0"
          class="flex-shrink-0 bg-n-brand text-white rounded-full px-1.5 py-0.5 text-[10px] font-medium leading-none"
          :aria-label="`${unread} ${t('INTERNAL_CHATS.UNREAD_MESSAGES')}`"
        >
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
          {{ unread > 99 ? '99+' : unread }}
        </span>
      </div>
    </div>
  </button>
</template>
